import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { CircularProgress } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { RobotoTableCell } from "@/shared/components";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";

import styles from "./SortableColumnHeader.module.css";

interface SortableColumnHeaderProps {
  /** The display name of the column shown to the user */
  columnName: string;
  /** The key identifying which column this header represents */
  columnKey: SortColumn;
  /** The currently active sort column */
  sortDirection: SortDirection;
  /** Whether this column is currently active */
  isActive: boolean;
  /** Whether the table is currently fetching data. When true, and this column is active, show a spinner instead of sort icon. */
  isFetching: boolean;

  onSort: (column: SortColumn, direction: SortDirection) => void;
}

export const SortableColumnHeader: React.FC<SortableColumnHeaderProps> = ({
  columnName,
  columnKey,
  sortDirection,
  isActive,
  isFetching,
  onSort,
}) => {
  const handleClick = () => {
    if (!isActive) {
      // Not currently sorted by this column -> start with ascending
      onSort(columnKey, "ASC");
    } else if (sortDirection === "ASC") {
      // Currently ascending -> switch to descending
      onSort(columnKey, "DESC");
    } else {
      // Currently descending -> cycle back to ASC
      onSort(columnKey, "ASC");
    }
  };

  const showSpinner = isFetching && isActive;

  return (
    <RobotoTableCell onClick={handleClick} className={styles.headerCell}>
      <div className={styles.contentWrapper}>
        {columnName}
        <div className={styles.sortIconContainer}>
          {showSpinner ? (
            <CircularProgress size={16} color="primary" />
          ) : (
            <ArrowUpwardIcon
              fontSize="small"
              className={classNames(styles.sortIcon, {
                [styles.active]: isActive,
                [styles.desc]: isActive && sortDirection === "DESC",
              })}
            />
          )}
        </div>
      </div>
    </RobotoTableCell>
  );
};
